import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Environment, ENVIRONMENT } from '@ld.consumer-portal/models';
import { map, Observable } from 'rxjs';

export interface ScreenshotMetadata {
  filename: string;
  path: string;
  category: string;
  name: string;
  viewport: 'desktop' | 'mobile' | 'tablet' | 'mobile-friendly' | 'unknown';
  route: string;
  stepOrder?: number;
}

@Injectable({ providedIn: 'root' })
export class ScreenshotService {
  private readonly http = inject(HttpClient);
  private readonly env = inject<Environment>(ENVIRONMENT);

  getScreenshots(): Observable<ScreenshotMetadata[]> {
    const basePath = this.env.appSettings.screenshotBasePath || '/screenshots/baseline/';
    return this.http.get<ScreenshotMetadata[]>(`${basePath}manifest.json`);
  }

  getScreenshotsByCategory(): Observable<Map<string, ScreenshotMetadata[]>> {
    return this.getScreenshots().pipe(
      map((screenshots) => {
        const grouped = new Map<string, ScreenshotMetadata[]>();
        screenshots.forEach((screenshot) => {
          const existing = grouped.get(screenshot.category);
          if (existing) {
            existing.push(screenshot);
          } else {
            grouped.set(screenshot.category, [screenshot]);
          }
        });
        return grouped;
      }),
    );
  }

  getCategories(): Observable<string[]> {
    return this.getScreenshots().pipe(
      map((screenshots) => {
        const categories = new Set<string>();
        screenshots.forEach((s) => categories.add(s.category));
        return Array.from(categories).sort();
      }),
    );
  }

  filterByViewport(
    screenshots: ScreenshotMetadata[],
    viewport: 'all' | 'desktop' | 'mobile' | 'tablet' | 'mobile-friendly',
  ): ScreenshotMetadata[] {
    if (viewport === 'all') {
      return screenshots;
    }
    return screenshots.filter((s) => s.viewport === viewport);
  }
}
